// apps/share-web/src/storage-persist.ts
//
// Asks the browser to keep this site's storage instead of evicting it.
//
// Why it matters here and not on a normal web page: the words of a song are
// read in a hall, often with no signal, and the whole point of caching them is
// that they are there when the band opens the app. Default Cache API storage is
// "best effort" — the browser may drop it under disk pressure, and iOS Safari
// clears site data that has not been touched for about seven days. That is
// exactly the interval between two Sundays.
//
// `persist()` is a REQUEST. Chrome grants it against its own engagement
// heuristics, Safari grants it on installation to the home screen, and either
// may say no. So this returns the answer rather than a boolean "worked":
// a surface that promises offline it cannot keep is worse than one that says
// what it has.
//
// Everything is swallowed. This runs during startup, and a rejected probe —
// Safari has historically thrown in private browsing — must not take the page
// down with it.

export type PersistResult =
  /** The browser will keep this site's storage until the user clears it. */
  | 'granted'
  /** The browser said no. Storage still works; it is just evictable. */
  | 'denied'
  /** No Storage API here, or it threw. Nothing was asked and nothing is known. */
  | 'unsupported';

export async function requestPersistentStorage(
  nav: Navigator = typeof navigator === 'undefined' ? ({} as Navigator) : navigator,
): Promise<PersistResult> {
  try {
    // The `nav?.storage` access is itself inside the try: a hardened private
    // mode, a locked-down WebView, or an extension that replaced the property
    // can make the GETTER throw, not just the methods below. That used to
    // happen before this try block, so it escaped as an unhandled rejection
    // from the fire-and-forget call site (`main.ts`) instead of being
    // swallowed like everything else here.
    const storage = nav?.storage;
    if (!storage || typeof storage.persisted !== 'function' || typeof storage.persist !== 'function') {
      return 'unsupported';
    }
    // Already persistent → don't ask again. Re-requesting every load costs a
    // permission evaluation for nothing, and on some browsers it is what
    // surfaces a prompt to a person who already said yes.
    if (await storage.persisted()) return 'granted';
    return (await storage.persist()) ? 'granted' : 'denied';
  } catch {
    return 'unsupported';
  }
}
